/* eslint-disable max-lines */
/* tslint:disable:max-line-length max-file-line-count */

import * as fromActions from '../actions/AddDirectory';

import {
  ADLdapConfigData,
  AddDirectory,
  WizardData
} from '../types/state/AddDirectory';
import {
  AbstractApiResponse,
  CreateADConfigTaskResponse,
  DirectoryType,
  DryRunResult,
  GroupSyncTaskReponse,
  RequestState,
  StepToIndex,
  UpdateAttributeMappingTaskResponse,
  UpdateDomainSelectionRequest,
  VidmDirectoryCompleteData,
  EnvironmentRequest
} from 'vrlcm-gui-types';
import {
  ToastItem,
  ToastType,
  pushAppLevelToast,
  removeOtherToast
} from '../common/components/push-app-level-toast';
import {
  all,
  call,
  put,
  select,
  takeEvery,
  takeLatest
} from 'redux-saga/effects';
import {
  directoryDataToTCData,
  getInitialWizardData,
  getInitialWizardState,
  mapAttrDataToUserMapping,
  persistedDataToWizardData,
  userGroupSelectionToApiData
} from '../mappers/directory-management';

import { DirectoryApi, EnvironmentApi } from 'vrlcm-gui-api';
// eslint-disable-next-line max-len
import { TestConnectionState } from '../components/directory-management/add-directory/steps/directory-detail-step/test-connection-button/TestConnectionState';
import { addDirectoryStateSelector } from '../selectors/add-directory-state-selector';
import { promiseDelay } from '../utils/promise-delay';
import { l10n } from 'i18n/i18nUtil';

interface StepProcessingResponse<T = any> {
  success: boolean;
  notifications: ToastItem[];
  data?: T;
}

function* waitForStateUpdate() {
  // Important. react processes actions in an async manner,
  // we shall wait for, actions to be processed by the reducer.
  yield call(promiseDelay, 1000);
}

/**
 * @param waitAndFetch - if there are any Redux Actions still being processed
 *                       then it is safe to wait before accessing state.
 */
function* fetchLatestWizardState(waitAndFetch = false) {
  if (waitAndFetch) {
    yield waitForStateUpdate();
  }
  const state: AddDirectory = yield select(addDirectoryStateSelector);
  return state;
}

/*
 * called on next of every step, useful for snooping wizard changes
 */
function* persistWizardData(requestState?: string) {
  yield;
}

function* moveToStep(step: number) {
  yield put(fromActions.Actions.updateWizardCurrentStep(step));
}

function* moveToNextStep(step: number) {
  yield moveToStep(step + 1);
}

function* enableNextStep(step: number) {
  yield put(
    fromActions.Actions.updateWizardStepState(step + 1, { disabled: false })
  );
}

function* markStepAsComplete(step: number) {
  yield put(
    fromActions.Actions.updateWizardStepState(step, { state: 'complete' })
  );
}
function* markWizardAsSubmitted() {
  yield put(fromActions.Actions.updateWizardLevelState({ submitted: true }));
}

function* happyTransitionToNextStep(
  currentStep: number,
  isSaveStepAndSync?: boolean
) {
  yield markStepAsComplete(currentStep);

  if (!isSaveStepAndSync) {
    yield enableNextStep(currentStep);
    yield moveToNextStep(currentStep);
  } else {
    let step = currentStep;
    while (step < StepToIndex.Preview) {
      yield enableNextStep(step);
      yield markStepAsComplete(step);
      step++;
    }
    yield moveToStep(StepToIndex.Preview);
  }
}
function* markWizardInprogress(message: string) {
  yield put(
    fromActions.Actions.updateWizardLevelState({
      inprogress: true,
      overlayMessage: message
    })
  );
}
function* unMarkWizardIInprogress() {
  yield put(
    fromActions.Actions.updateWizardLevelState({
      inprogress: false,
      overlayMessage: ''
    })
  );
}

function* fetchLatestDataFromVidmAndUpdateWizardData(
  directoryType: DirectoryType,
  directoryConfigId: string
) {
  const response: AbstractApiResponse<VidmDirectoryCompleteData> = yield call(
    DirectoryApi.getCompleteDirectoryDetail,
    {
      directoryConfigId,
      directoryType
    }
  );
  if (response.success && response.data) {
    yield put(
      fromActions.Actions.updateWizardData(
        persistedDataToWizardData(directoryConfigId, {
          directoryCompleteData: response.data
        })
      )
    );
  }
  const stepResponse: StepProcessingResponse = {
    success: !!(response.success && response.data),
    notifications: response.success
      ? []
      : (
          response.errors || [l10n('authzn.dirmgmt.fetchingDirDetailsFailed')]
        ).map(message => ({
          message,
          type: ToastType.Red,
          createdOn: response.when ? response.when : new Date()
        }))
  };
  return stepResponse;
}

function* handleInitializeWizard(
  action: ReturnType<typeof fromActions.Actions.initializeADWizardPage>
) {
  const opts = action.payload;

  const globalEnvironmentData: EnvironmentRequest = yield call(
    EnvironmentApi.getEnvironmentRequestById,
    'globalenvironment'
  );

  const vidmDetails = {
    isVidmFipsMode:
      globalEnvironmentData.products[0].properties.fipsMode === 'true',
    version: globalEnvironmentData.products[0].version
  };
  if (opts.update) {
    const { directoryConfigId, directoryType } = opts.update;

    const updateResponse: StepProcessingResponse = yield fetchLatestDataFromVidmAndUpdateWizardData(
      directoryType,
      directoryConfigId
    );

    if (updateResponse.success) {
      yield put(
        fromActions.Actions.initializeADWizardPageSuccess(
          getInitialWizardState(true),
          undefined,
          vidmDetails
        )
      );
    } else {
      yield put(fromActions.Actions.initializeADWizardPageFail());
    }
  } else if (opts.add) {
    const initialWizardData = getInitialWizardData(opts.add.directoryType);
    if (initialWizardData) {
      yield put(
        fromActions.Actions.initializeADWizardPageSuccess(
          getInitialWizardState(false),
          initialWizardData,
          vidmDetails
        )
      );
    } else {
      yield put(fromActions.Actions.initializeADWizardPageFail());
    }
  }
}

function* watchForInitializeWizard() {
  yield takeLatest(
    fromActions.INITIALIZE_AD_WIZARD_PAGE,
    handleInitializeWizard
  );
}

function* handleGroupSyncTrigger(
  action: ReturnType<typeof fromActions.Actions.groupSelectionTriggerSync>
) {
  const state: AddDirectory = yield fetchLatestWizardState();
  const { wizardData } = state;
  if (!wizardData) {
    return;
  }
  const { directoryConfigId } = wizardData;
  if (!directoryConfigId) {
    return;
  }
  const groupDN = action.payload;
  const response: AbstractApiResponse<GroupSyncTaskReponse> = yield call(
    DirectoryApi.searchSubGroups,
    directoryConfigId,
    groupDN
  );
  if (response.success && response.data) {
    yield put(
      fromActions.Actions.groupSelectionGroupSyncCompleted(
        groupDN,
        response.data.groups || [],
        new Date(),
        response.data.moreThan1000Groups
      )
    );
  } else {
    yield put(
      fromActions.Actions.groupSelectionGroupSyncFailed(
        groupDN,
        response.errors || [l10n('authzn.dirmgmt.groupSearchFailed')]
      )
    );
  }
}

function* watchForTriggerGroupSync() {
  yield takeEvery(
    fromActions.GROUP_SELECTION_TRIGGER_GROUP_SYNC,
    handleGroupSyncTrigger
  );
}

function* updateDirectoryConfigHandler(
  wizardData: WizardData,
  directoryConfigId: string
) {
  const response: AbstractApiResponse<CreateADConfigTaskResponse> = yield call(
    DirectoryApi.updateDirectoryConfig,
    directoryConfigId,
    directoryDataToTCData(wizardData.directoryData, wizardData.directoryType)
  );
  const stepResponse: StepProcessingResponse<CreateADConfigTaskResponse> = {
    success: !!(response.success && response.data),
    notifications: response.success
      ? [
          {
            type: ToastType.Green,
            message: l10n('authzn.dirmgmt.dirConfigUpdated'),
            createdOn: new Date()
          }
        ]
      : (response.errors || [l10n('authzn.dirmgmt.dirConfigUpdateFailed')]).map(
          message => ({
            message,
            type: ToastType.Red,
            createdOn: response.when ? response.when : new Date()
          })
        ),
    data: response.data
  };
  return stepResponse;
}

function* createDirectoryConfigHandler(wizardData: WizardData) {
  const response: AbstractApiResponse<CreateADConfigTaskResponse> = yield call(
    DirectoryApi.createDirectoryConfig,
    directoryDataToTCData(wizardData.directoryData, wizardData.directoryType)
  );
  const stepResponse: StepProcessingResponse<CreateADConfigTaskResponse> = {
    success: !!(response.success && response.data),
    notifications: response.success
      ? [
          {
            type: ToastType.Green,
            message: l10n('authzn.dirmgmt.dirConfigCreated'),
            createdOn: new Date()
          }
        ]
      : (response.errors || [l10n('authzn.dirmgmt.dirConfigCreateFailed')]).map(
          message => ({
            message,
            type: ToastType.Red,
            createdOn: response.when ? response.when : new Date()
          })
        ),
    data: response.data
  };
  return stepResponse;
}

function* processMapAttributeStepAndMoveNext(
  step: number,
  wizardData: WizardData,
  directoryConfigId: string
) {
  const { mapAttributeData } = wizardData;
  // update mapping in vIDM
  const response: AbstractApiResponse<UpdateAttributeMappingTaskResponse> = yield call(
    DirectoryApi.updateAttributeMapping,
    directoryConfigId,
    mapAttrDataToUserMapping(mapAttributeData)
  );

  const stepResponse: StepProcessingResponse = {
    success: response.success,
    notifications: response.success
      ? []
      : (response.errors || [l10n('authzn.dirmgmt.attrMappingFailed')]).map(
          message => ({
            message,
            type: ToastType.Red,
            createdOn: response.when ? response.when : new Date()
          })
        )
  };
  return stepResponse;
}

function* processDomainSelectionAndMoveNext(
  step: number,
  wizardData: WizardData,
  directoryConfigId: string
) {
  // update in vIDM
  const response: AbstractApiResponse = yield call(
    DirectoryApi.updateDomainSelection,
    {
      directoryConfigId,
      domains: wizardData.domainSelectionData.domains.filter(d => d.selected)
    } as UpdateDomainSelectionRequest
  );

  const stepResponse: StepProcessingResponse = {
    success: response.success,
    notifications: response.success
      ? []
      : (response.errors || [l10n('authzn.dirmgmt.domainSelectionFailed')]).map(
          message => ({
            message,
            type: ToastType.Red,
            createdOn: response.when ? response.when : new Date()
          })
        )
  };
  return stepResponse;
}

function* processUserDetailsAndMoveNext(
  wizardData: WizardData,
  directoryConfigId: string
) {
  const { userSelectionData } = wizardData;

  // update groups and users in vIDM
  const response: AbstractApiResponse<DryRunResult> = yield call(
    DirectoryApi.updateUserAndGroupMapping,
    userGroupSelectionToApiData({
      directoryConfigId,
      userSelectionData
    })
  );

  const stepResponse: StepProcessingResponse<DryRunResult> = {
    success: !!(response.success && response.data),
    notifications: response.success
      ? []
      : (
          response.errors || [l10n('authzn.dirmgmt.userDetailsUpdateFailed')]
        ).map(message => ({
          message,
          type: ToastType.Red,
          createdOn: response.when ? response.when : new Date()
        })),
    data: response.data
  };
  return stepResponse;
}

function* processGroupDetailsAndMoveNext(
  wizardData: WizardData,
  directoryConfigId: string,
  directoryType: DirectoryType
) {
  const { groupSelectionData } = wizardData;

  const containsGroupWithMoreThan1000SubGroups = groupSelectionData.selectedGroups.some(
    g => !!(g.syncResult.data && g.syncResult.data.moreThan1000Groups)
  );

  let groupDNToNumTotalSubGroupsMap = groupSelectionData.selectedGroups.reduce(
    (finalMap, g) => {
      finalMap[g.groupDN] = g.syncResult.data
        ? g.syncResult.data.totalSubGroups
        : 0;
      return finalMap;
    },
    {} as { [groupDN: string]: number }
  );

  if (containsGroupWithMoreThan1000SubGroups) {
    const directoryRes: AbstractApiResponse<VidmDirectoryCompleteData> = yield call(
      DirectoryApi.getCompleteDirectoryDetail,
      {
        directoryConfigId,
        directoryType,
        isGetBeforeUpdate: true
      }
    );
    if (
      directoryRes.data &&
      directoryRes.data &&
      directoryRes.data.groupProfileFromAd
    ) {
      groupDNToNumTotalSubGroupsMap = groupSelectionData.selectedGroups.reduce(
        (finalMap, g) => {
          finalMap[g.groupDN] =
            directoryRes.data &&
            directoryRes.data.groupProfileFromAd &&
            directoryRes.data.groupProfileFromAd.identityGroupInfo[
              g.groupDN
            ] !== undefined
              ? directoryRes.data.groupProfileFromAd.identityGroupInfo[
                  g.groupDN
                ].numTotal
              : g.syncResult.data
              ? g.syncResult.data.totalSubGroups
              : 0;
          return finalMap;
        },
        {} as { [groupDN: string]: number }
      );
    }
  }

  // update groups and users in vIDM
  const response: AbstractApiResponse<DryRunResult> = yield call(
    DirectoryApi.updateUserAndGroupMapping,
    userGroupSelectionToApiData({
      directoryConfigId,
      groupSelectionData,
      groupDNToNumTotalSubGroupsMap
    })
  );

  const stepResponse: StepProcessingResponse<DryRunResult> = {
    success: !!(response.success && response.data),
    notifications: response.success
      ? []
      : (
          response.errors || [l10n('authzn.dirmgmt.groupDetailsUpdateFailed')]
        ).map(message => ({
          message,
          type: ToastType.Red,
          createdOn: response.when ? response.when : new Date()
        })),
    data: response.data
  };
  return stepResponse;
}

function* handleMoveToNextStep(
  action: ReturnType<typeof fromActions.Actions.moveToNextStep>
) {
  const step = action.payload;
  yield happyTransitionToNextStep(step);
}

function* handleProcessCurrentStepAndMoveNext(
  action:
    | ReturnType<typeof fromActions.Actions.processCurrentStepAndMoveToNextStep>
    | ReturnType<typeof fromActions.Actions.saveStepAndSync>
) {
  const step = action.payload;
  const isSaveStepAndSync = action.type === fromActions.SAVE_STEP_AND_SYNC;
  yield markWizardInprogress('');
  const state: AddDirectory = yield fetchLatestWizardState(true);
  yield unMarkWizardIInprogress();
  const { wizardData } = state;
  if (!wizardData) {
    return;
  }
  const { directoryConfigId, directoryType } = wizardData;
  if (directoryConfigId) {
    // directory is already created, handle update operations.
    if (step === StepToIndex.DirectoryDetail) {
      yield markWizardInprogress(l10n('authzn.dirmgmt.updateDirectoryConfig'));
      const stepResponse: StepProcessingResponse<CreateADConfigTaskResponse> = yield call(
        updateDirectoryConfigHandler,
        wizardData,
        directoryConfigId
      );
      if (stepResponse.success && stepResponse.data) {
        // prep data for other steps
        const updateResponse: StepProcessingResponse = yield fetchLatestDataFromVidmAndUpdateWizardData(
          directoryType,
          directoryConfigId
        );

        if (updateResponse.success) {
          yield happyTransitionToNextStep(step, isSaveStepAndSync);
          yield persistWizardData();
        } else {
          pushAppLevelToast(updateResponse.notifications);
        }
      }
      pushAppLevelToast(stepResponse.notifications);
      yield unMarkWizardIInprogress();
    } else if (step === StepToIndex.DomainSelection) {
      yield markWizardInprogress(
        l10n(
          'authzn.tenantmgmt.common.updateDirectoryDataModal.updatingDomainDetails'
        )
      );
      const stepResponse: StepProcessingResponse = yield call(
        processDomainSelectionAndMoveNext,
        step,
        wizardData,
        directoryConfigId
      );
      if (stepResponse.success) {
        const updateResponse: StepProcessingResponse = yield fetchLatestDataFromVidmAndUpdateWizardData(
          directoryType,
          directoryConfigId
        );

        if (updateResponse.success) {
          yield happyTransitionToNextStep(step, isSaveStepAndSync);
          yield persistWizardData();
        } else {
          pushAppLevelToast(updateResponse.notifications);
        }
      }
      pushAppLevelToast(stepResponse.notifications);
      yield unMarkWizardIInprogress();
    } else if (step === StepToIndex.MapAttribute) {
      yield markWizardInprogress(
        l10n(
          'authzn.tenantmgmt.common.updateDirectoryDataModal.updatingAttrDetails'
        )
      );
      const stepResponse: StepProcessingResponse = yield call(
        processMapAttributeStepAndMoveNext,
        step,
        wizardData,
        directoryConfigId
      );
      if (stepResponse.success) {
        const updateResponse: StepProcessingResponse = yield fetchLatestDataFromVidmAndUpdateWizardData(
          directoryType,
          directoryConfigId
        );

        if (updateResponse.success) {
          yield happyTransitionToNextStep(step, isSaveStepAndSync);
          yield persistWizardData();
        } else {
          pushAppLevelToast(updateResponse.notifications);
        }
      }
      pushAppLevelToast(stepResponse.notifications);
      yield unMarkWizardIInprogress();
    } else if (step === StepToIndex.GroupSelection) {
      yield markWizardInprogress(
        l10n(
          'authzn.tenantmgmt.common.updateDirectoryDataModal.updatingGroupDetails'
        )
      );
      const stepResponse = yield call(
        processGroupDetailsAndMoveNext,
        wizardData,
        directoryConfigId,
        directoryType
      );
      // prep data for preview step
      if (stepResponse.success && stepResponse.data) {
        const updateResponse: StepProcessingResponse = yield fetchLatestDataFromVidmAndUpdateWizardData(
          directoryType,
          directoryConfigId
        );

        if (updateResponse.success) {
          yield happyTransitionToNextStep(step, isSaveStepAndSync);
          yield persistWizardData();
        } else {
          pushAppLevelToast(updateResponse.notifications);
        }
      }
      pushAppLevelToast(stepResponse.notifications);
      yield unMarkWizardIInprogress();
    } else if (step === StepToIndex.UserSelection) {
      yield markWizardInprogress(
        l10n(
          'authzn.tenantmgmt.common.updateDirectoryDataModal.updatingUserDetails'
        )
      );
      const stepResponse = yield call(
        processUserDetailsAndMoveNext,
        wizardData,
        directoryConfigId
      );
      yield happyTransitionToNextStep(step, isSaveStepAndSync);
      yield persistWizardData();
      pushAppLevelToast(stepResponse.notifications);
      yield unMarkWizardIInprogress();
    }
  }
}

function* watchForProcessCurrentStepAndMoveToNext() {
  yield takeLatest(
    fromActions.PROCESS_CURRENT_STEP_AND_MOVE_TO_NEXT_STEP,
    handleProcessCurrentStepAndMoveNext
  );
}

function* watchForSaveStepAndNext() {
  yield takeLatest(
    fromActions.SAVE_STEP_AND_SYNC,
    handleProcessCurrentStepAndMoveNext
  );
}

function* watchForMoveToNextStep() {
  yield takeLatest(fromActions.MOVE_TO_NEXT_STEP, handleMoveToNextStep);
}

function* handleCreateDirectoryAndMoveToNextStep() {
  const step = StepToIndex.DirectoryDetail;

  yield markWizardInprogress('');
  const state: AddDirectory = yield fetchLatestWizardState(true);
  yield unMarkWizardIInprogress();

  const { wizardData, wizardState } = state;
  if (!wizardData || !wizardState) {
    return;
  }

  yield markWizardInprogress(l10n('authzn.dirmgmt.creatingDirConfig'));

  const stepResponse: StepProcessingResponse<CreateADConfigTaskResponse> = yield call(
    createDirectoryConfigHandler,
    wizardData
  );
  if (stepResponse.success && stepResponse.data) {
    // prep data for other steps
    const updateResponse: StepProcessingResponse = yield fetchLatestDataFromVidmAndUpdateWizardData(
      wizardData.directoryType,
      stepResponse.data.directory.directoryConfigId
    );

    if (updateResponse.success) {
      yield happyTransitionToNextStep(step);
      yield persistWizardData();
      pushAppLevelToast(stepResponse.notifications);
    } else {
      pushAppLevelToast(updateResponse.notifications);
    }
  } else {
    pushAppLevelToast(stepResponse.notifications);
  }
  yield unMarkWizardIInprogress();
}

function* watchForCreateDirectoryAndMoveToNextStep() {
  yield takeLatest(
    fromActions.CREATE_DIRECTORY_AND_MOVE_TO_NEXT_STEP,
    handleCreateDirectoryAndMoveToNextStep
  );
}

function* handleSubmitWizard(
  action: ReturnType<typeof fromActions.Actions.submitWizard>
) {
  const editPage = action.payload;
  yield markWizardInprogress('');
  const state: AddDirectory = yield fetchLatestWizardState(true);
  yield unMarkWizardIInprogress();

  const { wizardData } = state;
  if (!wizardData) {
    return;
  }
  const { directoryConfigId } = wizardData;
  if (!directoryConfigId) {
    return;
  }

  yield markWizardInprogress(
    editPage
      ? l10n('authzn.dirmgmt.updatingDir')
      : l10n('authzn.dirmgmt.finishingSetUpDir')
  );

  // update groups and users in vIDM
  const syncResponse: AbstractApiResponse = yield call(
    DirectoryApi.triggerADSync,
    {
      directoryConfigId
    }
  );

  if (syncResponse.success) {
    yield persistWizardData(RequestState.COMPLETED);
    pushAppLevelToast([
      {
        type: ToastType.Green,
        message: editPage
          ? l10n('authzn.dirmgmt.dirUpdated')
          : l10n('authzn.dirmgmt.dirAdded'),
        createdOn: new Date()
      }
    ]);
    yield markWizardAsSubmitted();
  } else {
    yield unMarkWizardIInprogress();
    pushAppLevelToast(
      (syncResponse.errors || [l10n('authzn.dirmgmt.dirSetupFailed')]).map(
        message => ({
          message,
          type: ToastType.Red,
          createdOn: syncResponse.when ? syncResponse.when : new Date()
        })
      )
    );
  }
}

function* watchForSubmitWizard() {
  yield takeLatest(fromActions.SUBMIT_WIZARD, handleSubmitWizard);
}

function* handleTriggerTestConnection() {
  yield put(
    fromActions.Actions.updateTestConnectionData({
      testConnectionState: TestConnectionState.Running
    })
  );
  const state: AddDirectory = yield fetchLatestWizardState(true);
  const { wizardData } = state;
  if (!wizardData) {
    return;
  }
  const directoryData = wizardData.directoryData;
  const directoryType = wizardData.directoryType;
  try {
    const res: AbstractApiResponse = yield call(
      DirectoryApi.triggerTestConnection,
      directoryDataToTCData(directoryData as ADLdapConfigData, directoryType)
    );
    // raise wizard level notification
    if (res.success) {
      pushAppLevelToast([
        {
          type: ToastType.Green,
          message: l10n('authzn.dirmgmt.testConnectionPassed'),
          createdOn: res.when ? res.when : new Date()
        }
      ]);
    } else {
      const errors = res.errors || [
        l10n('authzn.dirmgmt.unknownErrorsOccured')
      ];
      removeOtherToast();
      pushAppLevelToast(
        errors.map(e => ({
          type: ToastType.Red,
          message: `${l10n('authzn.dirmgmt.testConnectionFailed')} ${e}`,
          createdOn: res.when ? res.when : new Date()
        }))
      );
    }

    // update test connection results in store.
    yield put(
      fromActions.Actions.updateTestConnectionData({
        when: new Date(),
        testConnectionState: res.success
          ? TestConnectionState.Completed
          : TestConnectionState.Failed,
        errors: res.errors,
        runOn: {
          ...directoryData
        }
      })
    );
  } catch (err) {
    pushAppLevelToast([
      {
        type: ToastType.Red,
        message: `${l10n(
          'authzn.dirmgmt.testConnFailedUnknown'
        )} - ${JSON.stringify(err)}`,
        createdOn: new Date()
      }
    ]);
  }
}

function* watchForTriggerTestConnection() {
  yield takeLatest(
    fromActions.TRIGGER_TEST_CONNECTION,
    handleTriggerTestConnection
  );
}

export default function* addDirectorySaga() {
  yield all([
    watchForInitializeWizard(),
    watchForTriggerGroupSync(),
    watchForTriggerTestConnection(),
    watchForProcessCurrentStepAndMoveToNext(),
    watchForSaveStepAndNext(),
    watchForMoveToNextStep(),
    watchForCreateDirectoryAndMoveToNextStep(),
    watchForSubmitWizard()
  ]);
}
